import type {
  AdminCrewReportCoveragePresentation,
  AdminCrewReportDeskBarValueKind,
  AdminCrewReportDeskCategory,
  AdminCrewReportMonthsOnBoardBucket,
  AdminCrewReportOwnerRepatriationCategory,
  AdminCrewReportPersonValueKind,
  AdminCrewReportReportedVesselsStatistics,
  AdminCrewReportStatisticsView,
  AdminCrewReportWomenCoverageStatistics,
} from "../models/admin-crew-report.models";
import {
  ADMIN_CREW_REPORT_DESK_COLORS,
  ADMIN_CREW_REPORT_DESK_FALLBACK_COLORS,
  ADMIN_CREW_REPORT_MONTHS_ON_BOARD_BUCKET_LABELS,
  ADMIN_CREW_REPORT_OWNER_REPATRIATION_AXIS_INTERVAL,
  ADMIN_CREW_REPORT_OWNER_REPATRIATION_SEGMENTS,
  ADMIN_CREW_REPORT_STATISTICS_PRINT_BODY_CLASS,
  ADMIN_CREW_REPORT_STATISTICS_PRINT_CLEANUP_TIMEOUT_MS,
  ADMIN_CREW_REPORT_STATISTICS_PRINT_PAGE_STYLE,
  ADMIN_CREW_REPORT_STATISTICS_PRINT_PAGE_STYLE_ID,
} from "./admin-crew-report.constants";

export function getAdminCrewReportMonthsOnBoardAccessibleSummary(
  buckets: AdminCrewReportMonthsOnBoardBucket[],
  personValueKind: AdminCrewReportPersonValueKind = "crew",
): string {
  return buckets
    .map(
      (bucket) =>
        `${ADMIN_CREW_REPORT_MONTHS_ON_BOARD_BUCKET_LABELS[bucket.key]}: ${formatAdminCrewReportPeopleCount(bucket.count, personValueKind)}, ${formatAdminCrewReportMonthsOnBoardPercentage(bucket)}`,
    )
    .join("; ");
}

export function getAdminCrewReportMonthsOnBoardBarHeight(percentage: number, maximumPercentage: number): number {
  if (percentage <= 0 || maximumPercentage <= 0) return 0;
  return Math.min((percentage / maximumPercentage) * 100, 100);
}

export function formatAdminCrewReportMonthsOnBoardPercentage(bucket: AdminCrewReportMonthsOnBoardBucket): string {
  return `${bucket.percentage.toFixed(1)}%`;
}

export function getAdminCrewReportByDesksAccessibleSummary(categories: AdminCrewReportDeskCategory[]): string {
  return getAdminCrewReportDeskBarAccessibleSummary(categories, "crew");
}

export function getAdminCrewReportRunningVesselsAccessibleSummary(categories: AdminCrewReportDeskCategory[]): string {
  return getAdminCrewReportDeskBarAccessibleSummary(categories, "vessel");
}

export function getAdminCrewReportReportingPercentage(percentage: number): number {
  if (!Number.isFinite(percentage)) return 0;
  return Math.min(Math.max(percentage, 0), 100);
}

export function formatAdminCrewReportReportingPercentage(percentage: number): string {
  return `${getAdminCrewReportReportingPercentage(percentage).toFixed(1)}%`;
}

export function formatAdminCrewReportVesselCount(count: number): string {
  return `${count} ${count === 1 ? "vessel" : "vessels"}`;
}

export function getAdminCrewReportReportingAccessibleSummary(
  statistics: AdminCrewReportReportedVesselsStatistics,
): string {
  const reportedVesselLabel = statistics.reportedVesselCount === 1 ? "vessel" : "vessels";
  const notReportedVesselLabel = statistics.notReportedVesselCount === 1 ? "vessel" : "vessels";

  return `${statistics.reportedVesselCount} ${reportedVesselLabel} reported out of ${statistics.totalRunningVesselCount} running vessels, ${formatAdminCrewReportReportingPercentage(statistics.reportedPercentage)}; ${statistics.notReportedVesselCount} ${notReportedVesselLabel} not reported, ${formatAdminCrewReportReportingPercentage(statistics.notReportedPercentage)}.`;
}

export function getAdminCrewReportCoveragePresentation(
  view: AdminCrewReportStatisticsView,
  statistics: AdminCrewReportReportedVesselsStatistics | AdminCrewReportWomenCoverageStatistics,
): AdminCrewReportCoveragePresentation {
  if (view === "women") {
    const womenStatistics = statistics as AdminCrewReportWomenCoverageStatistics;
    const withWomenVesselLabel = womenStatistics.vesselsWithWomenCount === 1 ? "vessel" : "vessels";
    const notConfirmedVesselLabel = womenStatistics.womenNotConfirmedVesselCount === 1 ? "vessel" : "vessels";

    return {
      accessibleSummary: `${womenStatistics.vesselsWithWomenCount} running ${withWomenVesselLabel} have at least one woman reported out of ${womenStatistics.totalRunningVesselCount} running vessels, ${formatAdminCrewReportReportingPercentage(womenStatistics.vesselsWithWomenPercentage)}; ${womenStatistics.womenNotConfirmedVesselCount} ${notConfirmedVesselLabel} do not have women confirmed, ${formatAdminCrewReportReportingPercentage(womenStatistics.womenNotConfirmedPercentage)}.`,
      gaugeTitle: "Vessels With Women",
      chartTitle: "Women Coverage",
      totalRunningVesselCount: womenStatistics.totalRunningVesselCount,
      primaryVesselCount: womenStatistics.vesselsWithWomenCount,
      secondaryVesselCount: womenStatistics.womenNotConfirmedVesselCount,
      primaryPercentage: womenStatistics.vesselsWithWomenPercentage,
      secondaryPercentage: womenStatistics.womenNotConfirmedPercentage,
      primaryLegendLabel: "Women reported",
      secondaryLegendLabel: "Women not confirmed",
      primaryPercentageLabel: "confirmed",
      countUnit: "vessel",
    };
  }

  const crewStatistics = statistics as AdminCrewReportReportedVesselsStatistics;
  return {
    accessibleSummary: getAdminCrewReportReportingAccessibleSummary(crewStatistics),
    gaugeTitle: "Reported Vessels",
    chartTitle: "Data Collection",
    totalRunningVesselCount: crewStatistics.totalRunningVesselCount,
    primaryVesselCount: crewStatistics.reportedVesselCount,
    secondaryVesselCount: crewStatistics.notReportedVesselCount,
    primaryPercentage: crewStatistics.reportedPercentage,
    secondaryPercentage: crewStatistics.notReportedPercentage,
    primaryLegendLabel: "Reported",
    secondaryLegendLabel: "Not reported",
    primaryPercentageLabel: "reported",
    countUnit: null,
  };
}

export function formatAdminCrewReportPeopleCount(
  count: number,
  personValueKind: AdminCrewReportPersonValueKind,
): string {
  if (personValueKind === "women") return `${count} ${count === 1 ? "woman" : "women"}`;
  return `${count} crew`;
}

export function getAdminCrewReportDeskBarAccessibleSummary(
  categories: AdminCrewReportDeskCategory[],
  valueKind: AdminCrewReportDeskBarValueKind,
): string {
  return categories
    .map((category) => {
      if (valueKind === "vessel") {
        return `${category.name}: ${category.value} ${category.value === 1 ? "vessel" : "vessels"}`;
      }
      return `${category.name}: ${formatAdminCrewReportPeopleCount(category.value, valueKind)}`;
    })
    .join("; ");
}

export function getAdminCrewReportDeskBarHeight(value: number, maximumValue: number): number {
  if (value <= 0 || maximumValue <= 0) return 0;
  return Math.min((value / maximumValue) * 100, 100);
}

export function getAdminCrewReportDeskBarGap(categoryCount: number): number {
  if (categoryCount <= 5) return 12;
  if (categoryCount <= 8) return 8;
  if (categoryCount <= 12) return 4;
  return 2;
}

export function getAdminCrewReportDeskColor(categoryName: string): string {
  const normalizedCategoryName = categoryName.trim().toUpperCase();
  const mappedColor = ADMIN_CREW_REPORT_DESK_COLORS[normalizedCategoryName];
  if (mappedColor) return mappedColor;

  let nameHash = 0;
  for (const character of normalizedCategoryName) {
    nameHash = (nameHash * 31 + character.charCodeAt(0)) >>> 0;
  }

  return ADMIN_CREW_REPORT_DESK_FALLBACK_COLORS[nameHash % ADMIN_CREW_REPORT_DESK_FALLBACK_COLORS.length];
}

export function getAdminCrewReportOwnerRepatriationAxisMaximum(
  owners: AdminCrewReportOwnerRepatriationCategory[],
): number {
  const largestOwnerTotal = Math.max(
    0,
    ...owners.map((owner) => (Number.isFinite(owner.totalVesselCount) ? owner.totalVesselCount : 0)),
  );

  return Math.max(
    ADMIN_CREW_REPORT_OWNER_REPATRIATION_AXIS_INTERVAL,
    Math.ceil(largestOwnerTotal / ADMIN_CREW_REPORT_OWNER_REPATRIATION_AXIS_INTERVAL) *
      ADMIN_CREW_REPORT_OWNER_REPATRIATION_AXIS_INTERVAL,
  );
}

export function getAdminCrewReportOwnerRepatriationAxisTicks(axisMaximum: number): number[] {
  if (!Number.isFinite(axisMaximum) || axisMaximum <= 0) return [0];

  const normalizedMaximum =
    Math.ceil(axisMaximum / ADMIN_CREW_REPORT_OWNER_REPATRIATION_AXIS_INTERVAL) *
    ADMIN_CREW_REPORT_OWNER_REPATRIATION_AXIS_INTERVAL;
  const tickCount = normalizedMaximum / ADMIN_CREW_REPORT_OWNER_REPATRIATION_AXIS_INTERVAL;

  return Array.from(
    { length: tickCount + 1 },
    (_, index) => index * ADMIN_CREW_REPORT_OWNER_REPATRIATION_AXIS_INTERVAL,
  );
}

export function getAdminCrewReportOwnerRepatriationSegmentWidth(count: number, axisMaximum: number): number {
  if (!Number.isFinite(count) || count <= 0 || !Number.isFinite(axisMaximum) || axisMaximum <= 0) return 0;
  return Math.min((count / axisMaximum) * 100, 100);
}

export function getAdminCrewReportOwnerRepatriationAccessibleSummary(
  owner: AdminCrewReportOwnerRepatriationCategory,
): string {
  const positiveBucketSummaries = ADMIN_CREW_REPORT_OWNER_REPATRIATION_SEGMENTS.flatMap((segment) => {
    const count = owner[segment.key];
    if (count <= 0) return [];
    return `${segment.label}: ${count} ${count === 1 ? "vessel" : "vessels"}`;
  });
  const totalLabel = `${owner.totalVesselCount} ${owner.totalVesselCount === 1 ? "vessel" : "vessels"} total`;

  return `${owner.ownerName}. ${[...positiveBucketSummaries, totalLabel].join("; ")}.`;
}

export function printAdminCrewReportStatisticsPdfExport(onCleanup: () => void): () => void {
  const printPageStyle = createAdminCrewReportStatisticsPrintPageStyle();
  let cleanupTimeoutId: number | undefined;
  let cleanupCompleted = false;

  const cleanupPrintMode = () => {
    if (cleanupCompleted) {
      return;
    }

    cleanupCompleted = true;
    window.removeEventListener("afterprint", cleanupPrintMode);
    if (cleanupTimeoutId !== undefined) {
      window.clearTimeout(cleanupTimeoutId);
    }
    document.body.classList.remove(ADMIN_CREW_REPORT_STATISTICS_PRINT_BODY_CLASS);
    printPageStyle.remove();
    onCleanup();
  };

  document.body.classList.add(ADMIN_CREW_REPORT_STATISTICS_PRINT_BODY_CLASS);
  window.addEventListener("afterprint", cleanupPrintMode, { once: true });

  try {
    window.print();
  } catch (error) {
    cleanupPrintMode();
    throw error;
  }

  if (!cleanupCompleted) {
    cleanupTimeoutId = window.setTimeout(cleanupPrintMode, ADMIN_CREW_REPORT_STATISTICS_PRINT_CLEANUP_TIMEOUT_MS);
  }

  return cleanupPrintMode;
}

function createAdminCrewReportStatisticsPrintPageStyle(): HTMLStyleElement {
  document.getElementById(ADMIN_CREW_REPORT_STATISTICS_PRINT_PAGE_STYLE_ID)?.remove();

  const styleElement = document.createElement("style");
  styleElement.id = ADMIN_CREW_REPORT_STATISTICS_PRINT_PAGE_STYLE_ID;
  styleElement.media = "print";
  styleElement.textContent = ADMIN_CREW_REPORT_STATISTICS_PRINT_PAGE_STYLE;
  document.head.appendChild(styleElement);

  return styleElement;
}
